import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface Aba {
  id: string;
  rotulo: string;
  conteudo: ReactNode;
}

/**
 * Abas acessíveis (padrão WAI-ARIA): setas ← → trocam de aba, Home/End vão à primeira/última,
 * e só a aba ativa entra na ordem do Tab.
 */
export function Abas({
  rotulo,
  abas,
  ativa,
  aoTrocar,
}: {
  rotulo: string;
  abas: Aba[];
  ativa: string;
  aoTrocar: (id: string) => void;
}) {
  const base = useId();
  const botoes = useRef<(HTMLButtonElement | null)[]>([]);
  const indiceAtivo = Math.max(
    abas.findIndex((a) => a.id === ativa),
    0,
  );

  const aoTeclar = (e: KeyboardEvent) => {
    const destino =
      e.key === 'ArrowRight'
        ? (indiceAtivo + 1) % abas.length
        : e.key === 'ArrowLeft'
          ? (indiceAtivo - 1 + abas.length) % abas.length
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? abas.length - 1
              : undefined;
    if (destino === undefined) return;
    e.preventDefault();
    const aba = abas[destino];
    if (aba) aoTrocar(aba.id);
    botoes.current[destino]?.focus();
  };

  const atual = abas[indiceAtivo];
  return (
    <div className="flex flex-col gap-4">
      <div
        role="tablist"
        aria-label={rotulo}
        className="flex gap-1 overflow-x-auto overflow-y-hidden border-b border-borda"
        onKeyDown={aoTeclar}
      >
        {abas.map((aba, i) => {
          const selecionada = i === indiceAtivo;
          return (
            <button
              key={aba.id}
              ref={(el) => {
                botoes.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`${base}-aba-${aba.id}`}
              aria-selected={selecionada}
              aria-controls={`${base}-painel-${aba.id}`}
              tabIndex={selecionada ? 0 : -1}
              onClick={() => aoTrocar(aba.id)}
              className={cn(
                '-mb-px min-h-11 border-b-2 px-4 text-sm font-medium whitespace-nowrap',
                selecionada
                  ? 'border-primaria text-primaria'
                  : 'border-transparent text-texto-suave hover:text-texto',
              )}
            >
              {aba.rotulo}
            </button>
          );
        })}
      </div>
      {atual && (
        <div
          role="tabpanel"
          id={`${base}-painel-${atual.id}`}
          aria-labelledby={`${base}-aba-${atual.id}`}
        >
          {atual.conteudo}
        </div>
      )}
    </div>
  );
}
